import { useCallback, useRef } from 'react'
import { useLidoSDK, useWithdrawalData } from '.'
import { useWithdrawalAllowanceInfo } from './useWithdrawalAllowanceInfo'
import { useIsContractAddress } from '~/hooks'
import { useAccount } from 'wagmi'
import {
  TransactionCallback,
  TransactionCallbackStage,
} from '@lidofinance/lido-ethereum-sdk'
import {
  useApprovalModalStateDispatch,
  useModalState,
  useModalToggle,
} from '~/state/application/hooks'
import { ApprovalProgress } from '~/components/ApprovalModal/ApproveStatusLabel'
import { RESET_STAGE_TIME } from '../constants'
import { parseEther } from 'viem'
import { ApplicationModal } from '~/state/application/reducer'
import { useTranslation } from 'react-i18next'
import { WithdrawToken } from '../Withdrawal/types'
import { ApprovalStatus } from '../types'
import { useGA } from '~/hooks'
import { getIsUserCancel } from '../utils'

export const useWithdrawRequest = (
  amount: string,
  token: WithdrawToken,
  setCurrentStage: (stage: TransactionCallbackStage) => void,
  showOrderProgress: boolean,
  setShowOrderProgress: (show: boolean) => void,
  setAmount: (amount: string) => void,
  approvalStatus: ApprovalStatus,
  setApprovalStatus: (status: ApprovalStatus) => void,
) => {
  const { t } = useTranslation()
  const { trackView, trackClick } = useGA()
  const { address: account } = useAccount()
  const { withdraw } = useLidoSDK()
  const isContractAddress = useIsContractAddress(account)
  const { allowance, needsApprove } = useWithdrawalAllowanceInfo(
    amount || '0',
    token,
  )

  const { mutate: refreshWithdrawalData } = useWithdrawalData()

  const updateApprovalState = useApprovalModalStateDispatch()
  const showOrderProgressRef = useRef(showOrderProgress)
  showOrderProgressRef.current = showOrderProgress

  const isApprovalFlow = isContractAddress || !!(allowance && !needsApprove)
  const isTokenLocked = !!(isApprovalFlow && needsApprove)

  const { progress } = useModalState(ApplicationModal.APPROVAL_STATUS)
  const toggleSubmitParamsModal = useModalToggle(
    ApplicationModal.APPROVAL_STATUS,
    {
      stack: true,
      modalState: {
        title: t('submitParamsModal.title'),
        description1: t('submitParamsModal.description_1'),
        description2: t('submitParamsModal.description_2'),
        showLogo: false,
        progress: ApprovalProgress.APPROVING,
      },
    },
  )
  const toggleAllowanceModal = useModalToggle(
    ApplicationModal.APPROVAL_STATUS,
    {
      stack: true,
      modalState: {
        title: t('toggleAllowanceModal.approve_title', {
          symbol: token,
        }),
        description1: t('toggleAllowanceModal.description_1'),
        description2: t('toggleAllowanceModal.description_2'),
        showLogo: false,
        progress: ApprovalProgress.APPROVING,
      },
    },
  )

  const approveToken = useCallback(
    async ({ amount, token }: { amount: string; token: WithdrawToken }) => {
      const txCallbackApproval: TransactionCallback = async ({ stage }) => {
        if (stage === TransactionCallbackStage.RECEIPT) {
          updateApprovalState({
            progress: ApprovalProgress.APPROVE_SUCCESS,
          })
          // 等待交易确认
          setApprovalStatus('approving')
        }
        if (
          stage === TransactionCallbackStage.DONE ||
          stage === TransactionCallbackStage.MULTISIG_DONE
        ) {
          updateApprovalState({
            progress: ApprovalProgress.APPROVE_SUCCESS,
          })
          setApprovalStatus('completed')
          setCurrentStage(null)
        }
      }

      setCurrentStage(TransactionCallbackStage.SIGN)
      const formattedAmount = parseEther(amount)

      try {
        toggleAllowanceModal()
        await withdraw.approval.approve({
          amount: formattedAmount,
          token,
          callback: txCallbackApproval,
        })
      } catch (error) {
        const errorMsg = error.message
        if (
          !errorMsg.includes('user rejected') &&
          !errorMsg.includes('user denied')
        ) {
          console.log('failed to approve', error)
        }
        updateApprovalState({
          progress: ApprovalProgress.APPROVE_REJECTED,
        })
        if (approvalStatus !== 'idle') {
          setApprovalStatus('idle')
        }
        setCurrentStage(null)
      }
    },
    [withdraw.approval],
  )

  const request = useCallback(
    async ({ amount, token }: { amount: string; token: WithdrawToken }) => {
      const txCallback: TransactionCallback = async ({ stage }) => {
        const targetStage = [
          TransactionCallbackStage.RECEIPT,
          TransactionCallbackStage.DONE,
          TransactionCallbackStage.MULTISIG_DONE,
          TransactionCallbackStage.ERROR,
        ]
        if (targetStage.includes(stage)) {
          setCurrentStage(stage)
        }

        switch (stage) {
          case TransactionCallbackStage.RECEIPT:
            trackClick('webtl_stk_withdraw_submit', {
              symbol: token,
              amount: amount,
            })
            updateApprovalState({
              progress: ApprovalProgress.APPROVE_SUCCESS,
            })
            setShowOrderProgress(true)
            break
          case TransactionCallbackStage.DONE:
          case TransactionCallbackStage.MULTISIG_DONE:
            // 使用 Safe Wallet 时没有 RECEIPT 阶段，直接从 SIGN 阶段跳到 MULTISIG_DONE 阶段
            if (
              progress !== ApprovalProgress.APPROVE_SUCCESS &&
              !showOrderProgressRef.current
            ) {
              trackClick('webtl_stk_withdraw_submit', {
                symbol: token,
                amount: amount,
              })
              updateApprovalState({
                progress: ApprovalProgress.APPROVE_SUCCESS,
              })
              setShowOrderProgress(true)
            }
            trackClick('webtl_stk_withdraw_confirm', {
              symbol: token,
              amount: amount,
              is_success: true,
            })

            refreshWithdrawalData()
            setTimeout(() => {
              setAmount('')
              setShowOrderProgress(false)
              setCurrentStage(null)
            }, RESET_STAGE_TIME)
            break
          default:
            break
        }
      }

      setCurrentStage(TransactionCallbackStage.SIGN)
      const formattedAmount = parseEther(amount)

      const txProps = {
        amount: formattedAmount,
        token,
        callback: txCallback,
      }

      try {
        if (isApprovalFlow) {
          toggleSubmitParamsModal()
          await withdraw.request.requestWithdrawal(txProps)
        } else {
          toggleSubmitParamsModal()
          const deadline = BigInt(Math.floor(Date.now() / 1000) + 86_400) // 1 day
          await withdraw.request.requestWithdrawalWithPermit({
            ...txProps,
            deadline,
          })
        }
      } catch (error) {
        const isUserCancel = getIsUserCancel(error)
        if (isUserCancel) {
          trackView('webtl_stk_withdraw_confirm_cancel')
        } else {
          trackClick('webtl_stk_withdraw_confirm', {
            symbol: token,
            amount: amount,
            is_success: false,
            message: error.message,
          })
        }

        updateApprovalState({
          progress: ApprovalProgress.APPROVE_REJECTED,
        })
        setTimeout(() => {
          setShowOrderProgress(false)
          setCurrentStage(null)
        }, RESET_STAGE_TIME)
      }
    },
    [isApprovalFlow, withdraw.request],
  )

  return {
    isApprovalFlow,
    isTokenLocked,
    request,
    approveToken,
  }
}
